/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { AiOutlineInfoCircle, AiOutlineQuestionCircle } from "react-icons/ai";
import { HiOutlineSwitchHorizontal } from "react-icons/hi";
import { IoMdExit } from "react-icons/io";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { ActiveEmailAtom } from "../atoms/ActiveEmailAtom";
import { RecipientAtom } from "../atoms/RecipientAtom";
import { InternalUserAtom } from "../atoms/UserAtom";
import useClickOutside from "../hook/useClickOutside";
import { useResetAllRecoil } from "../hook/useResetAllRecoil";
import { IOption } from "../types/CommonTypes";
import { CURRENT_VERSION, CURRENT_VERSION_DATE } from "../utils/CurrentVersion";
import {
  localStorageClear,
  localStorageRemoveItem,
} from "../utils/LocalStorage";
import { useUnsavedChangesBlocker } from "../utils/UnsavedChangesBlocker";
import AboutBerserkerModal from "./Modal/container/AboutBerserker";
import { useModal } from "./Modal/Modal";
import { CurrentRecipientTagsAtom } from "./NewEmail/Modals/Recipients";

const LogoutPopoverContainer = styled.div`
  position: absolute;
  background: white;
  z-index: 20;
  height: fit-content;
  width: 200px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  bottom: 28px;
  left: 30px;

  display: flex;
  flex-direction: column;
  padding: 5px;
`;

const Config = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 20px auto;

  padding: 10px;
  font-size: 16px;
  color: #2f2f2f;
  cursor: pointer;

  * {
    display: flex;
    align-items: center;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;
  }
`;

const LabelName = styled.div`
  margin-left: 10px;
`;

const AboutHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 10px 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const Subtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
`;

interface Props {
  closeDropView: () => void;
}

export default function LogoutPopover(props: Props): JSX.Element | null {
  const { closeDropView } = props;
  const router = useRouter();
  const wrapperRef = useClickOutside(() => closeDropView());
  const modal = useModal();
  const resetRecoil = useResetAllRecoil();
  const internalUser = useRecoilValue(InternalUserAtom);
  const resetSelectedSearch = useResetRecoilState(RecipientAtom);

  const { campaignUnsavedChangesBlocker } = useUnsavedChangesBlocker();

  const [selectedSavedSearch, setSelectedSavedSearch] = useState<IOption>({
    name: "",
    value: "",
  });
  const resetCurrentRecipientTags = useResetRecoilState(
    CurrentRecipientTagsAtom
  );
  const setActiveEmail = useSetRecoilState(ActiveEmailAtom);

  function logout() {
    localStorageClear();
    resetRecoil();
    router.push("/");
  }

  function handleAboutClick() {
    modal.pushModal(
      <AboutBerserkerModal />,
      <AboutHeader>
        <Title>About BerserkerMail</Title>
        <div style={{ display: "flex" }}>
          <Subtitle>Version {CURRENT_VERSION?.split("patch")[0]}</Subtitle>
          <Subtitle>&nbsp;({CURRENT_VERSION_DATE})</Subtitle>
        </div>
      </AboutHeader>
    );
  }

  function handleResetSearch() {
    localStorageRemoveItem("ATOM_CURRENT_RECIPIENTS");
    localStorageRemoveItem("ATOM_RECIPIENT");
    localStorageRemoveItem("SEARCH_INPUT");
    localStorageRemoveItem("USER_EMAIL");
    localStorageRemoveItem("USER_TOKEN");
    localStorageRemoveItem("USER_UID");
    setSelectedSavedSearch({
      name: "",
      value: "",
    });
    resetSelectedSearch();
    resetCurrentRecipientTags();
    setActiveEmail((prevState) => ({
      ...prevState,
      labels: [],
    }));
  }

  return (
    <LogoutPopoverContainer ref={wrapperRef}>
      <Config>
        {internalUser && internalUser.managedUserUids.length > 1 && (
          <Row
            onClick={() => {
              campaignUnsavedChangesBlocker(() => {
                router.push("/account-selection");
                handleResetSearch();
              });
            }}
          >
            <HiOutlineSwitchHorizontal size="18px" />

            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
              `}
            >
              <LabelName>Switch Account</LabelName>
              {/* <LabelName
                css={css`
                  font-size: 12px;
                  opacity: 0.8;
                `}
              >
                (Ctrl/Cmd + S)
              </LabelName> */}
            </div>
          </Row>
        )}
        <Row
          onClick={() => {
            closeDropView();
            handleAboutClick();
          }}
        >
          <AiOutlineInfoCircle size="18px" />
          <LabelName>About BerserkerMail</LabelName>
        </Row>
        <Row
          onClick={() => {
            campaignUnsavedChangesBlocker(() => {
              closeDropView();
              router.push("/getting-started");
            });
          }}
        >
          <AiOutlineQuestionCircle size="18px" />
          <LabelName>Getting Started</LabelName>
        </Row>
        <Row
          onClick={() => {
            campaignUnsavedChangesBlocker(() => {
              closeDropView();
              logout();
            });
          }}
        >
          <IoMdExit size="18px" />
          <LabelName>Logout</LabelName>
        </Row>
      </Config>
    </LogoutPopoverContainer>
  );
}
